/**
 * PF Revamp — draft adapter (React Query-shaped).
 *
 * Wraps the injectable `draftStore` singleton (`utils/purchaseFlowDrafts`) in
 * React Query hooks so the UI can subscribe to the drafts list, load a
 * specific draft, and mutate state with standard React Query semantics
 * (loading flags, error surfacing, optimistic cache invalidation).
 *
 * v2 migration: the hooks stay unchanged — only `utils/purchaseFlowDrafts` is
 * swapped from `LocalDraftStore` to a BE-backed adapter.
 *
 * W-22 — this module is also the ownership seam: `useDraftOwnerUserId` resolves
 * the signed-in platform user id and threads it into `list` / `save` / `create`
 * so device-wide localStorage drafts can't leak between accounts sharing a
 * browser. `get` is intentionally left unscoped — ids only reach the UI through
 * the already-filtered list, and gating it would break resuming legacy drafts.
 *
 * `PfDraftsEndpoints` is a placeholder enum kept for shape-parity with the
 * rest of the `*.query.ts` files; no real HTTP paths are wired in v1 because
 * everything lives in localStorage. It's intentionally empty — delete / fill
 * when the BE contract lands.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query'

import type { OrderDraftDTO } from 'models/purchaseFlowRevamp'
import { draftStore, type DraftMeta, type DraftScope } from 'utils/purchaseFlowDrafts'
import { useUserData } from 'components/contexts/UserDataContext'

/**
 * Placeholder enum kept for shape-parity with other `*.query.ts` files.
 * Populated when the BE contract ships (Module 3b). Intentionally minimal so
 * eslint/TS don't complain about an empty enum — a single commented member
 * keeps it discoverable via grep.
 */
export enum PfDraftsEndpoints {
  /** Not wired in v1 — LocalDraftStore is the backend. */
  UNUSED_V1 = 'UNUSED_V1',
}

export enum PfDraftsQueryKeys {
  DRAFTS_LIST = 'pf-drafts-list',
  DRAFT = 'pf-draft',
}

/**
 * W-22 / W-22b — resolves the two ids every draft this device writes is stamped
 * with: the signed-in browser user, and their currently selected workspace.
 *
 * This is the one seam where auth/context state meets the draft store:
 * `LocalDraftStore` stays pure and every store call below threads the scope in,
 * so no call site can forget it and silently reintroduce cross-account or
 * cross-workspace draft visibility.
 *
 * The user id is the ACTING user, not the admin-on-behalf-of target
 * (`OrderDraftDTO.targetUserId` / `workspaceId`).
 *
 * W-22b — the viewer's currently selected workspace, the second axis a
 * device-wide localStorage draft has to be scoped by.
 *
 * `ownerUserId` alone is not enough: one account can belong to several
 * workspaces, and switching workspace would otherwise keep offering the previous
 * workspace's in-progress orders (property address + cart) as resumable.
 *
 * `undefined` while the workspace is resolving, and for an admin — who has no
 * property workspace of their own. `isDraftVisibleTo` skips the workspace rule
 * in that case rather than hiding everything.
 */
function useDraftScope(): DraftScope {
  const { baseUserData, currentUserWorkspace } = useUserData()
  return { ownerUserId: baseUserData?.id, workspaceId: currentUserWorkspace?.id }
}

/** Query: list the saved draft metadata rows visible to the current user. */
export function useDrafts(): UseQueryResult<DraftMeta[]> {
  const scope = useDraftScope()

  return useQuery({
    // Both ids are part of the key: switching account OR workspace in the same
    // browser must not serve the previous scope's list from cache.
    queryKey: [PfDraftsQueryKeys.DRAFTS_LIST, scope.ownerUserId, scope.workspaceId],
    queryFn: () => draftStore.list(scope),
  })
}

/** Query: fetch a single draft DTO by id. Disabled while `draftId` is falsy. */
export function useDraft(draftId: string | undefined): UseQueryResult<OrderDraftDTO> {
  return useQuery({
    queryKey: [PfDraftsQueryKeys.DRAFT, draftId],
    queryFn: async () => {
      const dto = await draftStore.get(draftId!)
      if (!dto) throw new Error(`Draft not found: ${draftId}`)
      return dto
    },
    enabled: Boolean(draftId),
  })
}

interface SaveDraftVars {
  draftId: string
  dto: OrderDraftDTO
}

/**
 * Mutation: persist a draft body. Invalidates list + single-draft caches.
 *
 * Every save (re)stamps the signed-in owner (W-22), which is also what claims a
 * resumed legacy draft for whoever touched it.
 */
export function useSaveDraft(): UseMutationResult<void, Error, SaveDraftVars> {
  const client = useQueryClient()
  const scope = useDraftScope()

  return useMutation<void, Error, SaveDraftVars>({
    mutationFn: ({ draftId, dto }) => draftStore.save(draftId, dto, scope),
    onSuccess: (_data, { draftId }) => {
      client.invalidateQueries({ queryKey: [PfDraftsQueryKeys.DRAFTS_LIST] })
      client.invalidateQueries({ queryKey: [PfDraftsQueryKeys.DRAFT, draftId] })
    },
  })
}

/** Mutation: create a new empty draft row owned by the signed-in user (W-22). */
export function useCreateDraft(): UseMutationResult<string, Error, void> {
  const client = useQueryClient()
  const scope = useDraftScope()

  return useMutation<string, Error, void>({
    mutationFn: () => draftStore.create(scope),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: [PfDraftsQueryKeys.DRAFTS_LIST] })
    },
  })
}

/** Mutation: delete a draft by id. Invalidates list + removes single-draft cache. */
export function useDeleteDraft(): UseMutationResult<void, Error, string> {
  const client = useQueryClient()

  return useMutation<void, Error, string>({
    mutationFn: (draftId) => draftStore.delete(draftId),
    onSuccess: (_data, draftId) => {
      client.invalidateQueries({ queryKey: [PfDraftsQueryKeys.DRAFTS_LIST] })
      client.removeQueries({ queryKey: [PfDraftsQueryKeys.DRAFT, draftId] })
    },
  })
}
